import React from 'react';

const ExoticOptions = ({ currentLesson, setCurrentLesson, currentState, updateTutorialState, currentTutorialInfo, userStats, setUserStats, setTutorialComplete, tutorialComplete }) => {
  
  const moduleContent = {
    1: {
      title: "Barrier Options",
      icon: "🚧",
      content: {
        keyPoints: [
          "Knock-in and knock-out option structures",
          "Up-and-out, down-and-out, up-and-in, down-and-in",
          "Barrier monitoring and rebate features",
          "Pricing with Monte Carlo and PDEs"
        ],
        example: "BTC up-and-out call: Strike $50k, barrier $60k. Option becomes worthless if BTC touches $60k before expiration."
      },
      quiz: [
        {
          id: 'q1',
          question: "A knock-out option:",
          options: ["Activates when barrier is hit", "Expires when barrier is hit", "Never expires", "Has no barriers"],
          correct: 1,
          explanation: "Correct! Knock-out options expire worthless when the underlying hits the barrier level."
        },
        {
          id: 'q2',
          question: "Barrier options are typically:",
          options: ["More expensive than vanilla", "Cheaper than vanilla", "Same price as vanilla", "Cannot be priced"],
          correct: 1,
          explanation: "Correct! Barrier options are cheaper because the barrier feature limits their potential payoff."
        }
      ]
    },
    2: {
      title: "Asian Options",
      icon: "🌏",
      content: {
        keyPoints: [
          "Average price and average strike structures",
          "Arithmetic vs geometric averaging",
          "Path-dependent payoffs and vol reduction",
          "Applications in commodity and crypto markets"
        ],
        example: "BTC average price call: Payoff based on average BTC price over last 30 days vs $50k strike."
      },
      quiz: [
        {
          id: 'q1',
          question: "Asian options depend on:",
          options: ["Final price only", "Average price over time", "Maximum price", "Minimum price"],
          correct: 1,
          explanation: "Correct! Asian options have payoffs based on the average price of the underlying over time."
        },
        {
          id: 'q2',
          question: "Asian options typically have:",
          options: ["Higher volatility than vanilla", "Lower volatility than vanilla", "Same volatility as vanilla", "No volatility"],
          correct: 1,
          explanation: "Correct! Averaging reduces volatility, making Asian options less volatile than vanilla options."
        }
      ]
    },
    3: {
      title: "Binary and Digital Options",
      icon: "💎",
      content: {
        keyPoints: [
          "All-or-nothing payoff structures",
          "Cash-or-nothing vs asset-or-nothing",
          "Touch and no-touch variants",
          "High gamma and vega risks near barriers"
        ],
        example: "BTC binary call: Pay $1000 if BTC > $55k at expiration, $0 otherwise. No partial payoffs."
      },
      quiz: [
        {
          id: 'q1',
          question: "Binary options have:",
          options: ["Proportional payoffs", "All-or-nothing payoffs", "Unlimited upside", "Gradual increases"],
          correct: 1,
          explanation: "Correct! Binary options pay a fixed amount or nothing, with no proportional payoffs."
        },
        {
          id: 'q2',
          question: "Binary options have high risk from:",
          options: ["Time decay only", "Gamma near strikes", "Interest rates", "Dividends"],
          correct: 1,
          explanation: "Correct! Binary options have extreme gamma risk near the barrier or strike price."
        }
      ]
    },
    4: {
      title: "Lookback Options",
      icon: "👀",
      content: {
        keyPoints: [
          "Fixed and floating lookback structures",
          "Maximum and minimum price dependencies",
          "Path-dependent exotic payoffs",
          "Hindsight advantage and premium costs"
        ],
        example: "BTC lookback call: Strike = minimum BTC price during life. Payoff = (Final Price - Minimum Price)."
      },
      quiz: [
        {
          id: 'q1',
          question: "Lookback options depend on:",
          options: ["Current price only", "Maximum or minimum prices", "Average prices", "Future predictions"],
          correct: 1,
          explanation: "Correct! Lookback options use the maximum or minimum price achieved during the option's life."
        },
        {
          id: 'q2',
          question: "Lookback options are:",
          options: ["Cheaper than vanilla", "More expensive than vanilla", "Same price as vanilla", "Free to trade"],
          correct: 1,
          explanation: "Correct! Lookback options are expensive because they provide the benefit of perfect hindsight."
        }
      ]
    },
    5: {
      title: "Rainbow and Basket Options",
      icon: "🌈",
      content: {
        keyPoints: [
          "Multi-asset option structures",
          "Correlation effects and portfolio volatility",
          "Best-of, worst-of, and outperformance options",
          "Quanto and currency protection features"
        ],
        example: "Crypto basket option: Payoff based on equally-weighted performance of BTC, ETH, and SOL vs benchmark."
      },
      quiz: [
        {
          id: 'q1',
          question: "Rainbow options involve:",
          options: ["Single assets only", "Multiple underlying assets", "Color-based pricing", "Weather derivatives"],
          correct: 1,
          explanation: "Correct! Rainbow options have payoffs depending on multiple underlying assets."
        },
        {
          id: 'q2',
          question: "Correlation between assets in rainbow options:",
          options: ["Doesn't matter", "Significantly affects pricing", "Is always 1.0", "Is always 0.0"],
          correct: 1,
          explanation: "Correct! Correlation between underlying assets is crucial for pricing rainbow options."
        }
      ]
    },
    6: {
      title: "Compound and Chooser Options",
      icon: "🎭",
      content: {
        keyPoints: [
          "Options on options structures",
          "Call on call, put on put, call on put variations",
          "Chooser options with delayed decisions",
          "Compound volatility and time decay effects"
        ],
        example: "BTC compound option: Right to buy BTC $55k call in 30 days for $2k premium (option on option)."
      },
      quiz: [
        {
          id: 'q1',
          question: "Compound options are:",
          options: ["Options on stocks", "Options on options", "Complex calculations", "Multiple strikes"],
          correct: 1,
          explanation: "Correct! Compound options give the right to buy or sell another option."
        },
        {
          id: 'q2',
          question: "Chooser options allow:",
          options: ["Multiple strikes", "Delayed call/put decision", "Changing expiration", "Free cancellation"],
          correct: 1,
          explanation: "Correct! Chooser options let you decide whether it becomes a call or put at a future date."
        }
      ]
    },
    7: {
      title: "Structured Products and Hybrids",
      icon: "🏗️",
      content: {
        keyPoints: [
          "Principal-protected notes and capital guarantees",
          "Reverse convertible bonds and autocallables",
          "Participation rates and caps/floors",
          "Regulatory and operational considerations"
        ],
        example: "BTC autocallable note: 100% principal protection + 150% upside participation, auto-redeems if BTC up 20%."
      },
      quiz: [
        {
          id: 'q1',
          question: "Principal-protected notes guarantee:",
          options: ["Maximum returns", "Minimum capital return", "Perfect hedging", "No losses ever"],
          correct: 1,
          explanation: "Correct! Principal-protected notes guarantee return of initial capital, usually with upside participation."
        },
        {
          id: 'q2',
          question: "Autocallable features:",
          options: ["Never redeem early", "Automatically redeem under conditions", "Require manual exercise", "Eliminate all risk"],
          correct: 1,
          explanation: "Correct! Autocallable products automatically redeem early when predetermined conditions are met."
        }
      ]
    }
  };

  const currentModuleContent = moduleContent[currentLesson];
  if (!currentModuleContent) return null;

  if (currentState.phase === 'intro') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="text-6xl mb-4">{currentModuleContent.icon}</div>
          <h3 className="text-white text-2xl font-bold mb-2">Module {currentLesson}: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Advanced exotic option structures</p>
        </div>

        <div className="bg-atticus-navy-800 rounded-lg p-6 space-y-4">
          <div className="border-l-4 border-pink-500 pl-4">
            <h4 className="text-white font-bold mb-2">🌟 Exotic Concepts</h4>
            <div className="space-y-2">
              {currentModuleContent.content.keyPoints.map((point, index) => (
                <p key={index} className="text-gray-300 text-sm">• {point}</p>
              ))}
            </div>
          </div>

          <div className="bg-rose-500/20 border border-rose-500/50 rounded-lg p-4">
            <h5 className="text-rose-400 font-bold mb-2">🎯 Exotic Example</h5>
            <p className="text-white text-sm">{currentModuleContent.content.example}</p>
          </div>
        </div>

        <button
          onClick={() => updateTutorialState({ phase: 'quiz' })}
          className="w-full bg-pink-600 hover:bg-pink-700 text-white py-3 rounded-lg font-bold transition-colors"
        >
          Test Exotic Options Knowledge →
        </button>
      </div>
    );
  }

  if (currentState.phase === 'quiz') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-white text-2xl font-bold mb-2">Knowledge Check: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Master these exotic structures</p>
        </div>

        {currentModuleContent.quiz.map((question, qIndex) => {
          const userAnswer = currentState.quizAnswers[question.id];
          const isAnswered = userAnswer !== undefined;
          const isCorrect = userAnswer === question.correct;

          return (
            <div key={question.id} className="bg-atticus-navy-800 rounded-lg p-6">
              <div className="mb-4">
                <h4 className="text-white font-bold mb-3">Question {qIndex + 1}: {question.question}</h4>
                <div className="space-y-2">
                  {question.options.map((option, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => updateTutorialState({ 
                        quizAnswers: { ...currentState.quizAnswers, [question.id]: oIndex }
                      })}
                      disabled={isAnswered}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        isAnswered
                          ? oIndex === question.correct 
                            ? 'bg-green-500/20 border-green-500 text-green-300'
                            : oIndex === userAnswer
                              ? 'bg-red-500/20 border-red-500 text-red-300'
                              : 'bg-gray-700 border-gray-600 text-gray-400'
                          : 'bg-atticus-navy-700 border-gray-600 text-gray-300 hover:border-pink-500 hover:text-white'
                      }`}
                    >
                      {option}
                      {isAnswered && oIndex === question.correct && <span className="float-right text-green-400">✓</span>}
                      {isAnswered && oIndex === userAnswer && oIndex !== question.correct && <span className="float-right text-red-400">✗</span>}
                    </button>
                  ))}
                </div>
              </div>

              {isAnswered && (
                <div className={`p-3 rounded-lg border ${isCorrect ? 'bg-green-500/20 border-green-500' : 'bg-red-500/20 border-red-500'}`}>
                  <p className={`text-sm ${isCorrect ? 'text-green-300' : 'text-red-300'}`}>
                    {isCorrect ? '✅ Correct! ' : '❌ Incorrect. '}{question.explanation}
                  </p>
                </div>
              )}
            </div>
          );
        })}

        {Object.keys(currentState.quizAnswers).length === currentModuleContent.quiz.length && (
          <div className="text-center">
            {currentModuleContent.quiz.every(q => currentState.quizAnswers[q.id] === q.correct) ? (
              <div className="space-y-4">
                <div className="bg-green-500/20 border border-green-500 rounded-lg p-4">
                  <div className="text-green-400 font-bold text-lg">🎉 Module {currentLesson} Complete!</div>
                  <div className="text-green-300">You've mastered {currentModuleContent.title}!</div>
                </div>
                <button
                  onClick={() => {
                    const moduleXP = currentTutorialInfo.xp_per_module;
                    setUserStats(prev => ({
                      ...prev,
                      completed_lessons: [...prev.completed_lessons, `exotic_options_module_${currentLesson}`],
                      total_xp: prev.total_xp + moduleXP,
                      badges: currentLesson === 1 ? [...prev.badges, 'Exotic Trader'] : prev.badges
                    }));

                    if (currentLesson < currentTutorialInfo.modules) {
                      setCurrentLesson(currentLesson + 1);
                      updateTutorialState({ phase: 'intro', quizAnswers: {} });
                    } else {
                      setTutorialComplete(true);
                      setUserStats(prev => ({
                        ...prev,
                        completed_tutorials: [...prev.completed_tutorials, 'exotic_options'],
                        certification_progress: Math.min(prev.certification_progress + 15, 100),
                        badges: [...prev.badges, 'Exotic Options Master']
                      }));
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-bold transition-colors"
                >
                  {currentLesson < currentTutorialInfo.modules ? `Continue to Module ${currentLesson + 1} →` : 'Complete Tutorial →'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-orange-500/20 border border-orange-500 rounded-lg p-4">
                  <div className="text-orange-400 font-bold">📖 Review Required</div>
                  <div className="text-orange-300">Please review the material and try again.</div>
                </div>
                <button
                  onClick={() => updateTutorialState({ quizAnswers: {}, phase: 'intro' })}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-bold transition-colors"
                >
                  Review Module {currentLesson}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default ExoticOptions;
